// src/components/news/NewsCard.jsx
import React from "react";
import { Link } from "react-router-dom";
import { CalendarIcon, TagIcon } from "@heroicons/react/24/outline";
import { classifyNewsNavHref } from "../../utils/newsLink";

const cardBaseClassName =
  "block rounded-2xl border shadow-xl p-6 md:p-8 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] cursor-pointer group support-media-card-fade-in";

/** Odd cards (1st, 3rd, …): mist; even cards (2nd, 4th, …): lake */
const cardSurfaceClassName = (index) =>
  index % 2 === 0
    ? "bg-gradient-to-br from-white via-mist-50/95 to-mist-100/80 border-mist-200/70"
    : "bg-gradient-to-br from-lake-50 via-lake-100/80 to-mist-50/90 border-lake-200/70";

const NewsCard = ({ newsItem, index = 0 }) => {
  if (!newsItem) return null;

  const rawUrl = (newsItem.url || "").trim();
  const nav = classifyNewsNavHref(rawUrl);

  // Format date to DD-MMM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const cardInner = (
    <div className="flex flex-col md:flex-row md:items-start gap-4">
        {/* Date and Product Badge */}
        <div className="flex flex-col sm:flex-row gap-3 md:flex-col md:min-w-[180px]">
          {newsItem.date && (
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-green-600">
                {formatDate(newsItem.date)}
              </span>
            </div>
          )}
          {newsItem.product && (
            <div className="flex items-center gap-2">
              <TagIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                {newsItem.product}
              </span>
            </div>
          )}
        </div>

        {/* Title and Summary */}
        <div className="flex-1">
          {newsItem.title && (
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-200">
              {newsItem.title}
            </h3>
          )}
          {newsItem.subtitle && (
            <p className="text-gray-600 text-base md:text-lg leading-relaxed line-clamp-2">
              {newsItem.subtitle}
            </p>
          )}
        </div>
      </div>
  );

  const animationStyle = {
    "--support-media-delay": `${index * 75}ms`,
  };

  const linkClassName = `${cardBaseClassName} ${cardSurfaceClassName(index)}`;

  if (nav.kind === "external") {
    const isMailOrTel = /^(mailto:|tel:)/i.test(nav.href);
    return (
      <a
        href={nav.href}
        className={linkClassName}
        style={animationStyle}
        {...(isMailOrTel
          ? {}
          : { target: "_blank", rel: "noopener noreferrer" })}
      >
        {cardInner}
      </a>
    );
  }

  if (nav.kind === "internal") {
    return (
      <Link to={nav.to} className={linkClassName} style={animationStyle}>
        {cardInner}
      </Link>
    );
  }

  return (
    <Link
      to={`/news/${newsItem.id || newsItem.documentId}`}
      className={linkClassName}
      style={animationStyle}
    >
      {cardInner}
    </Link>
  );
};

export default NewsCard;

